import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { X } from 'lucide-react';
import { Input } from './Input';
import { Button } from './Button';
import { MultiSelectModal } from './MultiSelectModal';
import { cn } from './cn';

export interface SearchSelectOption {
  value: string;
  label: string;
  hint?: string | undefined;
  thumbnailUrl?: string | null | undefined;
}

export interface SearchSelectPage {
  items: ReadonlyArray<SearchSelectOption>;
  hasMore: boolean;
  total?: number;
}

export type SearchSelectLoader = (
  query: string,
  page: number,
) => Promise<SearchSelectPage>;

interface SingleProps {
  multi?: false;
  value: string | null;
  onChange: (value: string | null) => void;
}

interface MultiProps {
  multi: true;
  value: ReadonlyArray<string>;
  onChange: (value: ReadonlyArray<string>) => void;
}

export type SearchSelectMode = 'inline' | 'modal';

type SearchSelectFieldProps = (SingleProps | MultiProps) & {
  loadOptions: SearchSelectLoader;
  resolveOption: (value: string) => Promise<SearchSelectOption | null>;
  placeholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  loadingMoreMessage?: string;
  loadMoreLabel?: string;
  errorMessage?: string | null;
  disabled?: boolean;
  inputId?: string;
  ariaLabel?: string;
  testId?: string;
  excludeValues?: ReadonlyArray<string>;
  debounceMs?: number;
  emptyHint?: ReactNode;
  /**
   * Multi 모드 전용. 'inline' 은 chip 누적 + dropdown (기본, 호환).
   * 'modal' 은 trigger 가 "N건 선택됨" + "선택 변경" 버튼만 표시하고,
   * 클릭 시 MultiSelectModal 다이얼로그가 열린다 (30+ 선택 시 layout 보호).
   */
  mode?: SearchSelectMode;
  modalTitle?: string;
  modalDescription?: string;
};

const DEFAULT_DEBOUNCE = 250;
const LOAD_MORE_THRESHOLD_PX = 80;

const useDebounced = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    if (value === debounced) return;
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay, debounced]);
  return debounced;
};

interface PageState {
  items: ReadonlyArray<SearchSelectOption>;
  page: number;
  hasMore: boolean;
  loading: boolean;
  error: Error | null;
}

const INITIAL_PAGE_STATE: PageState = {
  items: [],
  page: -1,
  hasMore: false,
  loading: false,
  error: null,
};

const usePagedOptions = (
  loadOptions: SearchSelectLoader,
  query: string,
  enabled: boolean,
): {
  state: PageState;
  loadMore: () => void;
} => {
  const [state, setState] = useState<PageState>(INITIAL_PAGE_STATE);
  const inflightRef = useRef<{ query: string; page: number } | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    inflightRef.current = { query, page: 0 };
    setState({ ...INITIAL_PAGE_STATE, loading: true });

    loadOptions(query, 0)
      .then((res) => {
        if (!active) return;
        setState({
          items: res.items,
          page: 0,
          hasMore: res.hasMore,
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (!active) return;
        setState({
          items: [],
          page: 0,
          hasMore: false,
          loading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      })
      .finally(() => {
        if (active && inflightRef.current?.query === query) {
          inflightRef.current = null;
        }
      });

    return () => {
      active = false;
    };
  }, [loadOptions, query, enabled]);

  const loadMore = useCallback(() => {
    setState((prev) => {
      if (prev.loading || !prev.hasMore || prev.page < 0) return prev;
      const nextPage = prev.page + 1;
      if (inflightRef.current?.page === nextPage) return prev;
      inflightRef.current = { query, page: nextPage };

      loadOptions(query, nextPage)
        .then((res) => {
          setState((curr) => {
            if (inflightRef.current?.query !== query) return curr;
            const seen = new Set(curr.items.map((it) => it.value));
            const merged = [
              ...curr.items,
              ...res.items.filter((it) => !seen.has(it.value)),
            ];
            return {
              items: merged,
              page: nextPage,
              hasMore: res.hasMore,
              loading: false,
              error: null,
            };
          });
        })
        .catch((err: unknown) => {
          setState((curr) => ({
            ...curr,
            loading: false,
            error: err instanceof Error ? err : new Error(String(err)),
          }));
        })
        .finally(() => {
          if (inflightRef.current?.page === nextPage) {
            inflightRef.current = null;
          }
        });

      return { ...prev, loading: true };
    });
  }, [loadOptions, query]);

  return { state, loadMore };
};

const useResolvedSingle = (
  resolveOption: SearchSelectFieldProps['resolveOption'],
  value: string | null,
): SearchSelectOption | null => {
  const [resolved, setResolved] = useState<SearchSelectOption | null>(null);
  useEffect(() => {
    if (!value) {
      setResolved(null);
      return;
    }
    let active = true;
    resolveOption(value)
      .then((r) => {
        if (active) setResolved(r);
      })
      .catch(() => {
        if (active) setResolved({ value, label: value });
      });
    return () => {
      active = false;
    };
  }, [resolveOption, value]);
  return resolved;
};

const useResolvedMulti = (
  resolveOption: SearchSelectFieldProps['resolveOption'],
  values: ReadonlyArray<string>,
): Record<string, SearchSelectOption> => {
  const [cache, setCache] = useState<Record<string, SearchSelectOption>>({});
  useEffect(() => {
    const missing = values.filter((v) => !cache[v]);
    if (missing.length === 0) return;
    let active = true;
    Promise.all(
      missing.map(async (v) => {
        const r = await resolveOption(v).catch(() => null);
        return [v, r ?? { value: v, label: v }] as const;
      }),
    ).then((pairs) => {
      if (!active) return;
      setCache((prev) => {
        const next = { ...prev };
        pairs.forEach(([k, v]) => {
          next[k] = v;
        });
        return next;
      });
    });
    return () => {
      active = false;
    };
  }, [resolveOption, values, cache]);
  return cache;
};

const SelectedSinglePill = ({
  option,
  disabled,
  onClear,
}: {
  option: SearchSelectOption;
  disabled?: boolean | undefined;
  onClear: () => void;
}) => (
  <div
    className="flex min-h-[var(--control-h-md)] items-center justify-between rounded-md border px-3 py-1"
    style={{
      borderColor: 'var(--divider-strong)',
      background: 'var(--surface-0)',
    }}
  >
    <div className="flex items-center gap-2.5">
      {option.thumbnailUrl ? (
        <img
          src={option.thumbnailUrl}
          alt=""
          className="h-7 w-7 rounded object-cover"
          style={{ background: 'var(--surface-1)' }}
        />
      ) : null}
      <div className="flex flex-col gap-0.5">
        <span className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
          {option.label}
        </span>
        {option.hint ? (
          <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
            {option.hint}
          </span>
        ) : null}
      </div>
    </div>
    <button
      type="button"
      onClick={onClear}
      disabled={disabled}
      aria-label="선택 해제"
      className="rounded-full px-1 text-sm focus-visible:outline-none focus-visible:ring-2"
      style={{ color: 'var(--content-muted)' }}
    >
      <X size={14} />
    </button>
  </div>
);

const ChipList = ({
  options,
  disabled,
  onRemove,
}: {
  options: ReadonlyArray<SearchSelectOption>;
  disabled?: boolean | undefined;
  onRemove: (value: string) => void;
}) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => (
      <span
        key={opt.value}
        className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs"
        style={{
          background: 'var(--surface-1)',
          border: '1px solid var(--divider-strong)',
          color: 'var(--content-strong)',
        }}
      >
        <span className="max-w-[200px] truncate">{opt.label}</span>
        <button
          type="button"
          onClick={() => onRemove(opt.value)}
          disabled={disabled}
          aria-label={`${opt.label} 제거`}
          className={cn(
            'rounded-full p-0.5 transition-transform duration-[var(--motion-fast)]',
            'hover:scale-110 hover:text-[color:var(--accent-default)]',
            'focus-visible:outline-2 focus-visible:outline-offset-2',
            'focus-visible:outline focus-visible:outline-[color:var(--accent-default)]',
          )}
          style={{ color: 'var(--content-muted)' }}
        >
          <X size={12} />
        </button>
      </span>
    ))}
  </div>
);

interface OptionRowProps {
  option: SearchSelectOption;
  active: boolean;
  selected: boolean;
  onPick: (opt: SearchSelectOption) => void;
  onHover: () => void;
  optionId: string;
}

const OptionRow = memo(
  ({ option, active, selected, onPick, onHover, optionId }: OptionRowProps) => (
    <li className="relative">
      {active ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-1 left-0 w-[3px] rounded-r"
          style={{ background: 'var(--accent-default)' }}
        />
      ) : null}
      <button
        type="button"
        role="option"
        id={optionId}
        aria-selected={selected}
        className={cn(
          'flex w-full items-center gap-3 py-2 pl-4 pr-3 text-left text-sm',
          'transition-colors duration-[var(--motion-fast)]',
          'hover:bg-[color:var(--surface-1)]',
          'focus-visible:outline-none',
          active && 'bg-[color:var(--surface-1)]',
          selected && 'font-medium',
        )}
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={onHover}
        onClick={() => onPick(option)}
      >
        {option.thumbnailUrl ? (
          <img
            src={option.thumbnailUrl}
            alt=""
            className="h-8 w-8 rounded object-cover"
            style={{
              background: 'var(--surface-1)',
              boxShadow: 'inset 0 0 0 1px var(--divider-strong)',
            }}
          />
        ) : null}
        <span className="flex flex-1 flex-col">
          <span style={{ color: 'var(--content-strong)' }}>{option.label}</span>
          {option.hint ? (
            <span className="text-xs" style={{ color: 'var(--content-muted)' }}>
              {option.hint}
            </span>
          ) : null}
        </span>
      </button>
    </li>
  ),
);
OptionRow.displayName = 'OptionRow';

interface DropdownProps {
  loading: boolean;
  error: Error | null;
  items: ReadonlyArray<SearchSelectOption>;
  hasMore: boolean;
  selectedValues: ReadonlySet<string>;
  activeIndex: number;
  loadingMessage: string;
  loadingMoreMessage: string;
  loadMoreLabel: string;
  emptyMessage: string;
  listboxId: string;
  onPick: (opt: SearchSelectOption) => void;
  onActivate: (index: number) => void;
  onLoadMore: () => void;
}

const Dropdown = ({
  loading,
  error,
  items,
  hasMore,
  selectedValues,
  activeIndex,
  loadingMessage,
  loadingMoreMessage,
  loadMoreLabel,
  emptyMessage,
  listboxId,
  onPick,
  onActivate,
  onLoadMore,
}: DropdownProps) => {
  const scrollRef = useRef<HTMLUListElement>(null);
  const sentinelRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          onLoadMore();
        }
      },
      {
        root,
        rootMargin: `0px 0px ${LOAD_MORE_THRESHOLD_PX}px 0px`,
        threshold: 0.01,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, items.length]);

  const isFirstLoad = loading && items.length === 0;
  const showEmpty = !loading && !error && items.length === 0;

  return (
    <ul
      ref={scrollRef}
      role="listbox"
      id={listboxId}
      className="max-h-72 overflow-y-auto rounded-md text-sm"
    >
      {isFirstLoad ? (
        <li className="px-3 py-2 text-xs" style={{ color: 'var(--content-muted)' }}>
          {loadingMessage}
        </li>
      ) : null}

      {error ? (
        <li className="px-3 py-2 text-xs" style={{ color: 'var(--signal-danger)' }}>
          {error.message}
        </li>
      ) : null}

      {showEmpty ? (
        <li className="px-3 py-2 text-xs" style={{ color: 'var(--content-muted)' }}>
          {emptyMessage}
        </li>
      ) : null}

      {items.map((opt, idx) => (
        <OptionRow
          key={opt.value}
          option={opt}
          active={idx === activeIndex}
          selected={selectedValues.has(opt.value)}
          optionId={`${listboxId}-opt-${idx}`}
          onPick={onPick}
          onHover={() => onActivate(idx)}
        />
      ))}

      {hasMore ? (
        <li ref={sentinelRef} className="px-3 py-2">
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onLoadMore}
            disabled={loading}
            className={cn(
              'w-full rounded border border-dashed px-2 py-1.5 text-center text-xs',
              'transition-colors duration-[var(--motion-fast)]',
              'hover:border-[color:var(--accent-default)] hover:text-[color:var(--accent-default)]',
              'focus-visible:outline-2 focus-visible:outline-offset-2',
              'focus-visible:outline focus-visible:outline-[color:var(--accent-default)]',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
            style={{
              color: 'var(--content-muted)',
              borderColor: 'var(--divider-strong)',
            }}
          >
            {loading ? loadingMoreMessage : loadMoreLabel}
          </button>
        </li>
      ) : null}
    </ul>
  );
};

const useListboxId = (testId: string | undefined): string => {
  const fallback = useId();
  return testId ? `${testId}-listbox` : `searchselect-${fallback}`;
};

interface MultiModeFrameProps {
  testId?: string | undefined;
  disabled?: boolean | undefined;
  selectedCount: number;
  chipOptions: ReadonlyArray<SearchSelectOption>;
  emptyHint: ReactNode;
  loadOptions: SearchSelectLoader;
  resolveOption: (value: string) => Promise<SearchSelectOption | null>;
  value: ReadonlyArray<string>;
  onChange: (next: ReadonlyArray<string>) => void;
  title: string;
  description?: string;
  placeholder: string;
  emptyMessage: string;
  loadingMessage: string;
  loadMoreLabel: string;
  debounceMs: number;
  excludeValues?: ReadonlyArray<string>;
  onRemoveChip: (value: string) => void;
  renderError: ReactNode;
}

const MultiModeFrame = ({
  testId,
  disabled,
  selectedCount,
  chipOptions,
  emptyHint,
  loadOptions,
  resolveOption,
  value,
  onChange,
  title,
  description,
  placeholder,
  emptyMessage,
  loadingMessage,
  loadMoreLabel,
  debounceMs,
  excludeValues,
  onRemoveChip,
  renderError,
}: MultiModeFrameProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2" data-testid={testId ? `${testId}-root` : undefined}>
      <div
        className="flex min-h-[var(--control-h-md)] items-center justify-between gap-3 rounded-md border px-3 py-1"
        style={{
          borderColor: 'var(--divider-strong)',
          background: 'var(--surface-0)',
        }}
      >
        <span className="text-[13px]" style={{ color: 'var(--content-strong)' }}>
          {selectedCount === 0
            ? '선택된 항목 없음'
            : `${selectedCount}건 선택됨`}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => setModalOpen(true)}
          data-testid={testId ? `${testId}-open-modal` : undefined}
        >
          선택 변경
        </Button>
      </div>

      <MultiSelectModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        loadOptions={loadOptions}
        resolveOption={resolveOption}
        initialValue={value}
        onApply={onChange}
        title={title}
        {...(description !== undefined ? { description } : {})}
        searchPlaceholder={placeholder}
        emptyMessage={emptyMessage}
        loadingMessage={loadingMessage}
        loadMoreLabel={loadMoreLabel}
        debounceMs={debounceMs}
        {...(excludeValues !== undefined ? { excludeValues } : {})}
        {...(emptyHint !== undefined && emptyHint !== null
          ? { emptySelectionHint: emptyHint }
          : {})}
      />

      {chipOptions.length > 0 ? (
        <ChipList options={chipOptions} disabled={disabled} onRemove={onRemoveChip} />
      ) : emptyHint ? (
        <p
          className="rounded p-3 text-center text-xs"
          style={{
            background: 'var(--surface-0)',
            border: '1px dashed var(--divider-strong)',
            color: 'var(--content-muted)',
          }}
        >
          {emptyHint}
        </p>
      ) : null}

      {renderError}
    </div>
  );
};

export const SearchSelectField = (props: SearchSelectFieldProps) => {
  const {
    loadOptions,
    resolveOption,
    placeholder = '검색',
    emptyMessage = '검색 결과가 없습니다',
    loadingMessage = '검색 중...',
    loadingMoreMessage = '추가 로드 중...',
    loadMoreLabel = '더 보기',
    errorMessage,
    disabled,
    inputId,
    ariaLabel,
    testId,
    excludeValues,
    debounceMs = DEFAULT_DEBOUNCE,
    emptyHint,
    mode = 'inline',
    modalTitle,
    modalDescription,
  } = props;

  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  /** 입력칸(앵커) — 여기를 누를 때만 드롭다운을 열어 둔다. */
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const debounced = useDebounced(keyword, debounceMs);
  const listboxId = useListboxId(testId);

  const isMulti = props.multi === true;
  const selectedSingle = useResolvedSingle(resolveOption, isMulti ? null : props.value);
  const multiValues = isMulti ? props.value : [];
  const resolvedMulti = useResolvedMulti(resolveOption, multiValues);

  const fetchEnabled = open && (isMulti || !selectedSingle);
  const { state, loadMore } = usePagedOptions(loadOptions, debounced, fetchEnabled);

  const excludeSet = useMemo(
    () =>
      new Set<string>([
        ...(excludeValues ?? []),
        ...(isMulti ? multiValues : []),
      ]),
    [excludeValues, isMulti, multiValues],
  );

  const items = useMemo(
    () => state.items.filter((opt) => !excludeSet.has(opt.value)),
    [state.items, excludeSet],
  );

  const selectedValueSet = useMemo(
    () =>
      new Set<string>(
        isMulti ? multiValues : selectedSingle ? [selectedSingle.value] : [],
      ),
    [isMulti, multiValues, selectedSingle],
  );

  useEffect(() => {
    setActiveIndex((idx) => Math.min(idx, Math.max(items.length - 1, 0)));
  }, [items.length]);

  useEffect(() => {
    if (!open) setActiveIndex(0);
  }, [open]);

  const pickSingle = useCallback(
    (opt: SearchSelectOption) => {
      if (props.multi) return;
      props.onChange(opt.value);
      setKeyword('');
      setOpen(false);
    },
    [props],
  );

  const pickMulti = useCallback(
    (opt: SearchSelectOption) => {
      if (!props.multi) return;
      if (props.value.includes(opt.value)) return;
      props.onChange([...props.value, opt.value]);
      setKeyword('');
    },
    [props],
  );

  const pick = isMulti ? pickMulti : pickSingle;

  const removeMulti = useCallback(
    (value: string) => {
      if (!props.multi) return;
      props.onChange(props.value.filter((v) => v !== value));
    },
    [props],
  );

  const clearSingle = useCallback(() => {
    if (props.multi) return;
    props.onChange(null);
    setKeyword('');
  }, [props]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
        setActiveIndex((i) => Math.min(i + 1, Math.max(items.length - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        if (!open || items.length === 0) return;
        e.preventDefault();
        const target = items[activeIndex];
        if (target) pick(target);
      } else if (e.key === 'Escape') {
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
      }
    },
    [activeIndex, items, open, pick],
  );

  const handleInputChange = useCallback((value: string) => {
    setKeyword(value);
    setOpen(true);
    setActiveIndex(0);
  }, []);

  const renderInput = () => (
    <Input
      id={inputId}
      aria-label={ariaLabel}
      aria-autocomplete="list"
      aria-controls={listboxId}
      aria-expanded={open}
      role="combobox"
      data-testid={testId}
      value={keyword}
      onChange={(e) => handleInputChange(e.target.value)}
      onFocus={() => setOpen(true)}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      autoComplete="off"
    />
  );

  const trigger = !isMulti && selectedSingle ? (
    <SelectedSinglePill option={selectedSingle} disabled={disabled} onClear={clearSingle} />
  ) : (
    renderInput()
  );

  const dropdownOpen = open && !(props.multi !== true && selectedSingle);

  const chipOptions = isMulti
    ? multiValues.map((v) => resolvedMulti[v] ?? { value: v, label: v })
    : [];

  const renderError = errorMessage ? (
    <span className="text-xs" style={{ color: 'var(--signal-danger)' }}>
      {errorMessage}
    </span>
  ) : null;

  if (isMulti && mode === 'modal' && props.multi) {
    return (
      <MultiModeFrame
        testId={testId}
        disabled={disabled}
        selectedCount={multiValues.length}
        chipOptions={chipOptions}
        emptyHint={emptyHint}
        loadOptions={loadOptions}
        resolveOption={resolveOption}
        value={props.value}
        onChange={props.onChange}
        title={modalTitle ?? '항목 선택'}
        {...(modalDescription !== undefined ? { description: modalDescription } : {})}
        placeholder={placeholder}
        emptyMessage={emptyMessage}
        loadingMessage={loadingMessage}
        loadMoreLabel={loadMoreLabel}
        debounceMs={debounceMs}
        {...(excludeValues !== undefined ? { excludeValues } : {})}
        onRemoveChip={removeMulti}
        renderError={renderError}
      />
    );
  }

  return (
    <div
      className={cn('flex flex-col', isMulti ? 'gap-2' : 'gap-1')}
      data-testid={testId ? `${testId}-root` : undefined}
    >
      <Popover.Root
        open={dropdownOpen}
        onOpenChange={(next) => {
          if (!next) setOpen(false);
        }}
      >
        <Popover.Anchor asChild>
          <div ref={anchorRef} className="w-full">{trigger}</div>
        </Popover.Anchor>
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            sideOffset={4}
            align="start"
            avoidCollisions
            collisionPadding={8}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              // 입력칸을 누르거나 입력칸에 포커스가 남을 때는 닫지 않고, 그 밖은 닫는다(QA-322).
              // 종전엔 e.currentTarget(Radix 가 이벤트를 쏘는 바깥 요소 자신)으로 검사해 항상 막혀,
              // 페이지를 떠나기 전까지 목록이 닫히지 않았다.
              const target = e.target as Node | null;
              if (target && anchorRef.current?.contains(target)) e.preventDefault();
            }}
            className={cn(
              'z-50 overflow-hidden rounded-md border outline-none',
              'data-[state=open]:animate-[searchselect-in_180ms_ease-out]',
              'data-[state=closed]:animate-[searchselect-out_120ms_ease-in]',
            )}
            style={{
              width: 'var(--radix-popover-trigger-width)',
              minWidth: '360px',
              background: 'var(--surface-raised)',
              borderColor: 'var(--divider-strong)',
              boxShadow: 'var(--elevation-2)',
            }}
            data-testid={testId ? `${testId}-dropdown` : undefined}
          >
            <Dropdown
              loading={state.loading}
              error={state.error}
              items={items}
              hasMore={state.hasMore}
              selectedValues={selectedValueSet}
              activeIndex={activeIndex}
              loadingMessage={loadingMessage}
              loadingMoreMessage={loadingMoreMessage}
              loadMoreLabel={loadMoreLabel}
              emptyMessage={emptyMessage}
              listboxId={listboxId}
              onPick={pick}
              onActivate={setActiveIndex}
              onLoadMore={loadMore}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {isMulti ? (
        chipOptions.length === 0 ? (
          emptyHint ? (
            <p
              className="rounded p-3 text-center text-xs"
              style={{
                background: 'var(--surface-0)',
                border: '1px dashed var(--divider-strong)',
                color: 'var(--content-muted)',
              }}
            >
              {emptyHint}
            </p>
          ) : null
        ) : (
          <ChipList options={chipOptions} disabled={disabled} onRemove={removeMulti} />
        )
      ) : null}

      {renderError}
    </div>
  );
};
